<script setup async>
import {computed} from "vue";
import {useHeadService} from '~/composables/useHeadService';
import {useAuthService} from '~/composables/useAuthService';
import {useStatsPage} from '~/composables/useStatsPage';
import FrontendHeader from "~/components/layout/FrontendHeader.vue";
import StatsView from "~/components/stats/StatsView.vue";
import ShortLinkShareWrapper from "~/components/share/ShortLinkShareWrapper.vue";
import ArchivedNotice from "~/components/ArchivedNotice.vue";

definePageMeta({
  name: 'ClubStats',
  path: '/clubs/:clubId/stats',
});

const route = useRoute();
const router = useRouter();
const clubId = route.params.clubId;

const {page, stats} = await useStatsPage(
    'club',
    clubId,
    'api/v2/page/club',
    {clubIds: [clubId]},
    'requestsUpdated'
);

const {setMeta} = useHeadService();
setMeta(
    page.value?.club?.name + ' Club Stats · Club' + (page.value?.club?.hasFixedAddress ? ' based in ' + page.value?.club?.city : '') + ' on Tap Leagues',
    page?.value?.club?.name + ' is a club' + (page?.value?.club?.hasFixedAddress ? ' based in ' + page?.value?.club?.city : '') + ' on Tap Leagues. View detailed stats for ' + page?.value?.club?.name + ' here.'
);

const {isAdminOfClub} = useAuthService();
const clubAdmin = computed(() => isAdminOfClub(page.value?.club?.id));

// The club has been deleted (archived) - show the deleted notice instead of content.
const clubArchived = computed(() => !!page.value?.club?.archivedAt);
const clubPrivate = computed(() => !!page.value?.privateToViewer);
usePrivatePage(clubPrivate);

const titleActions = computed(() => clubAdmin.value
    ? [{
      title: 'Edit club',
      icon: 'mdi-pencil',
      click: () => router.push({name: 'ClubDashboard', params: {clubId: page.value.club.id}}),
      color: 'info',
    }]
    : []);
</script>

<template>
  <base-page>
    <!-- Deleted (archived) club - show the notice instead of any content -->
    <!-- Members only for this viewer (PRIVACY_PLAN.md §4.1): the stub keeps the name and the join link -->
    <v-container v-if="clubPrivate" class="large">
      <frontend-header
          :image-file-name="page.club?.imageFileName"
          title-icon="mdi-account-group"
          :club="page.club ?? (page.clubId ? {id: page.clubId, slug: page.clubId, name: page.clubName} : null)"
          :title="page.club.name + ' · Stats'"
          :sub-title="`${page.club.playerCount} members${page.club.city ? ' · ' + page.club.city : ''}`"
      />
      <private-notice class="mt-5" reason="club" entity="club" :name="page.club.name" :join-slug="page.club.visibleInviteLink?.slug"/>
    </v-container>
    <v-container v-else-if="clubArchived" class="large">
      <archived-notice entity="club" :name="page.club.name"/>
    </v-container>
    <template v-else>
    <v-container class="large">
      <frontend-header
          :image-file-name="page.club?.imageFileName"
          title-icon="mdi-account-group"
          :club="page.club"
          :title="page.club.name + ' · Stats'"
          :sub-title="`${page.club.playerCount} members${page.club.city ? ' · ' + page.club.city : ''} · Statistics`"
          :actions="titleActions"
      />

      <div class="tl-action-strip mt-4">
        <short-link-share-wrapper>
          <template #default="{ click, loading: shareLoading }">
            <button class="as" @click="click" :disabled="shareLoading">
              <v-icon size="17" icon="mdi-share-variant"/>Share
            </button>
          </template>
        </short-link-share-wrapper>

        <router-link class="as" :to="{ name: 'ClubDetail', params: { clubId: page.club.id } }">
          <v-icon size="17" icon="mdi-account-group"/>Club
        </router-link>

        <router-link class="as" :to="{ name: 'FixtureSearch', query: { clubIds: [page.club.id] } }">
          <v-icon size="17" icon="mdi-scoreboard"/>Matches
        </router-link>
      </div>
    </v-container>

    <v-container class="large" v-if="stats && stats.length">
      <stats-view :stats="stats" form-mode="recent"/>
    </v-container>
    </template>
  </base-page>
</template>
